// access-check.js -- #1509. Single job: ask /api/beta-status once whether one user may use SlipSync right now.
// Reads: /api/beta-status (POST, the live session). Writes: nothing; a failed check is a capped warning.
// Returns { allowed, status } only for a well-formed answer about THAT user (the server names whose answer it is,
// #1510 Codex r1: authedApiFetch reads the live session, which can be another account mid-switch); anything else is
// null, meaning unknown -- never a guessed allow or deny. Bounded like the consent read: a stalled request is
// unknown after ACCESS_CHECK_TIMEOUT_MS, so the card never sits on "Connecting..." (pre-Codex Sec F2 / FC H1).
// Does NOT: show copy, cache, or decide what unknown means (useSlipSync proceeds on unknown; /api/slipsync refuses
// an unapproved sync itself).
import * as Sentry from '../errorReporting';
import { authedApiFetch } from '../api';

export const ACCESS_CHECK_TIMEOUT_MS = 10000;
// A failed check is recoverable (the user taps again; the server still gates the sync), so it is a warning
// (CC-IMPL-037), and at most MAX_WARNINGS per session: an outage must not turn every tap into an event (Sec F1).
const MAX_WARNINGS = 3;
let warningsSent = 0;

function warn(reason) {
  if (warningsSent >= MAX_WARNINGS) return;
  warningsSent += 1;
  Sentry.captureMessage('SlipSync access check failed', { level: 'warning', tags: { function: 'useSlipSync', stage: 'access', reason } });
}

export function _resetAccessCheckWarnings() {
  warningsSent = 0;
}

const TIMED_OUT = Symbol('timed out');

export async function checkSlipSyncAccess(userId) {
  if (!userId) return null;
  let timer;
  let res;
  try {
    res = await Promise.race([
      authedApiFetch('/api/beta-status', { method: 'POST' }),
      new Promise((resolve) => { timer = setTimeout(() => resolve(TIMED_OUT), ACCESS_CHECK_TIMEOUT_MS); }),
    ]);
  } catch {
    warn('network');
    return null;
  } finally {
    clearTimeout(timer);
  }
  if (res === TIMED_OUT) { warn('timeout'); return null; }
  if (!res || !res.ok) return null;
  let data;
  try { data = await res.json(); } catch { warn('body'); return null; }
  if (!data || data.user_id !== userId || typeof data.allowed !== 'boolean') return null;
  return { allowed: data.allowed, status: typeof data.status === 'string' ? data.status : null };
}
